/**
 * DAS2 Submission Controller (EPIC-023 / 23.5 — §12).
 *
 * Pilote le formulaire « Saisie de l'accusé de réception » de la page
 * `/das2-declaration.html` :
 *   - lit `dgfipReceiptNumber` + `submittedAt` depuis les champs.
 *   - génère un `Idempotency-Key` (UUID v4) via `crypto.randomUUID()`,
 *     persistance dans sessionStorage sous la clé
 *     `nm.das2.submission.{year}.idempotencyKey` — réutilisé sur retry,
 *     effacé après succès.
 *   - POST `/das2/declarations/{year}/submit` avec header
 *     `Idempotency-Key`.
 *   - codes 409 / 422 / 423 : messages distincts (cf. specs §12).
 *
 * Soumission unique : à succès, le formulaire est désactivé jusqu'à
 * rechargement de la page.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

export default class Das2SubmissionController extends BaseController {
  static targets = [
    "formContainer",
    "form",
    "receiptNumber",
    "submittedAt",
    "submitButton",
    "errorMessage",
    "successMessage",
  ];

  static values = {
    year: { type: Number, default: 0 },
  };

  connect() {
    this._currentYear = this.yearValue || this._readYearFromQuery() || (new Date().getFullYear() - 1);
    if (this.hasSubmittedAtTarget && !this.submittedAtTarget.value) {
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      this.submittedAtTarget.value = iso;
    }
  }

  async submit(event) {
    if (event) event.preventDefault();
    this._clearMessages();

    const receiptNumber = this.hasReceiptNumberTarget ? this.receiptNumberTarget.value.trim() : "";
    const submittedAt = this.hasSubmittedAtTarget ? this.submittedAtTarget.value.trim() : "";

    if (!receiptNumber) {
      this._showError("Indiquez le n° d'accusé DGFiP.");
      return;
    }
    if (!submittedAt) {
      this._showError("Indiquez la date de télédéclaration.");
      return;
    }

    const idempotencyKey = this._getOrCreateIdempotencyKey();
    this._toggleSubmitting(true);

    try {
      await api.das2.submit(
        this._currentYear,
        { dgfipReceiptNumber: receiptNumber, submittedAt },
        idempotencyKey,
      );
      this._clearIdempotencyKey();
      this._showSuccess(receiptNumber);
    } catch (error) {
      this._renderServerError(error);
    } finally {
      this._toggleSubmitting(false);
    }
  }

  _readYearFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("year");
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  _getOrCreateIdempotencyKey() {
    const storageKey = `nm.das2.submission.${this._currentYear}.idempotencyKey`;
    let key = null;
    try {
      key = sessionStorage.getItem(storageKey);
    } catch (e) {
      key = null;
    }
    if (!key) {
      key = (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
        ? crypto.randomUUID()
        : this._fallbackUuid();
      try {
        sessionStorage.setItem(storageKey, key);
      } catch (e) {
        // sessionStorage indisponible — la clé n'est pas persistée mais
        // l'appel courant utilise quand même la valeur générée.
      }
    }
    return key;
  }

  _clearIdempotencyKey() {
    const storageKey = `nm.das2.submission.${this._currentYear}.idempotencyKey`;
    try {
      sessionStorage.removeItem(storageKey);
    } catch (e) {
      // no-op
    }
  }

  _fallbackUuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.hidden = false;
    }
  }

  _showSuccess(receiptNumber) {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.textContent =
        `Déclaration DAS2 ${this._currentYear} enregistrée. Accusé n° ${receiptNumber}.`;
      this.successMessageTarget.hidden = false;
    }
    if (this.hasReceiptNumberTarget) this.receiptNumberTarget.disabled = true;
    if (this.hasSubmittedAtTarget) this.submittedAtTarget.disabled = true;
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
  }

  _clearMessages() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "";
      this.errorMessageTarget.hidden = true;
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.textContent = "";
      this.successMessageTarget.hidden = true;
    }
  }

  _toggleSubmitting(busy) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = busy;
      this.submitButtonTarget.textContent = busy ? "Enregistrement…" : "Enregistrer l'accusé";
    }
  }

  _renderServerError(error) {
    if (!(error instanceof APIError)) {
      this._showError("Erreur réseau, réessayez.");
      return;
    }
    if (error.status === 409) {
      this._showError("Cette déclaration a déjà été soumise avec un accusé différent.");
      return;
    }
    if (error.status === 422) {
      const pendingCount = error.data?.error?.details?.pendingCount;
      if (pendingCount && pendingCount > 0) {
        this._showError(`${pendingCount} bénéficiaire(s) sont encore à valider. Revenez sur la liste pour les compléter.`);
        return;
      }
      this._showError(error.message || "Validation refusée par le serveur.");
      return;
    }
    if (error.status === 423) {
      const at = error.data?.error?.details?.submittedAt;
      const when = at ? this.formatShortDate(at) : "précédemment";
      this._showError(`Cette déclaration a déjà été soumise le ${when}.`);
      return;
    }
    this._showError(error.message || "Échec de la soumission.");
  }
}
